import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Users, MapPin, Calendar, Building2, DollarSign, FileText, Filter, X, AlertTriangle, ExternalLink, Copy, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [upload, setUpload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [allowResend, setAllowResend] = useState(false);
  
  // Filter states
  const [selectedName, setSelectedName] = useState("all");
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [selectedDate, setSelectedDate] = useState("all");

  // Check if any employee has zero fixed charge
  const hasZeroFixedCharge = useMemo(() => {
    if (!upload?.employees) return false;
    return upload.employees.some(emp => emp.fixed_charge === 0 || emp.fixed_charge === 0.0);
  }, [upload]);

  // Copy to clipboard function
  const copyToClipboard = (value) => {
    navigator.clipboard.writeText(String(value));
    toast.success(`"${value}" kopyalandı!`);
  };

  // Fetch settings to check allow_resend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setAllowResend(response.data.allow_resend || false);
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchUploadDetail = async () => {
      try {
        const response = await axios.get(`${API}/uploads/${id}`);
        setUpload(response.data);
      } catch (error) {
        console.error("Error fetching upload detail:", error);
        toast.error("Veri yüklenemedi");
      } finally {
        setLoading(false);
      }
    };

    fetchUploadDetail();
  }, [id]);

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API}/export/${id}/${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${upload.filename}.${format === 'json' ? 'json' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${format.toUpperCase()} başarıyla indirildi!`);
    } catch (error) {
      console.error(`Error exporting ${format}:`, error);
      toast.error("Export hatası");
    }
  };

  const handleSendToServer = async () => {
    setSending(true);
    try {
      // Send directly to external API from frontend
      const response = await fetch('https://www.crmportal.be/api/invoices/ingest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(upload.raw_json)
      });
      
      const result = await response.json();
      
      if (response.ok && (result.success || result.status === 'ok')) {
        // Update status in our database
        await axios.post(`${API}/send-to-server/${id}`);
        toast.success("Sunucuya başarıyla gönderildi!");
        // Refresh data
        const refreshResponse = await axios.get(`${API}/uploads/${id}`);
        setUpload(refreshResponse.data);
      } else {
        toast.error(result.message || "Sunucu hatası");
      }
    } catch (error) {
      console.error("Error sending to server:", error);
      toast.error("Sunucuya gönderim hatası: " + (error.message || ""));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  if (!upload) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="not-found-message">
        <p className="text-xl text-gray-500">Veri bulunamadı</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Button
              data-testid="back-button"
              variant="outline"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Geri Dön
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                sessionStorage.removeItem("hyk_auth");
                navigate("/login");
              }}
              className="text-gray-600 hover:text-red-600 hover:border-red-600"
            >
              Çıkış Yap
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              data-testid="preview-pdf-button"
              variant="outline"
              onClick={() => window.open(`${API}/uploads/${id}/download`, '_blank')}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              PDF Görüntüle
            </Button>
            <Button
              data-testid="export-json-button"
              variant="outline"
              onClick={() => handleExport('json')}
            >
              <Download className="w-4 h-4 mr-2" />
              JSON İndir
            </Button>
            <Button
              data-testid="export-excel-button"
              onClick={() => handleExport('excel')}
            >
              <Download className="w-4 h-4 mr-2" />
              Excel İndir
            </Button>
            {upload.sent_status !== 'sent' && (
              <Button
                data-testid="send-to-server-button"
                onClick={handleSendToServer}
                disabled={sending}
                className="bg-green-600 hover:bg-green-700"
              >
                <Building2 className="w-4 h-4 mr-2" />
                {sending ? 'Gönderiliyor...' : 'Sunucuya Gönder'}
              </Button>
            )}
            {upload.sent_status === 'sent' && (
              <div className="flex items-center gap-2">
                <span className="px-4 py-2 bg-green-100 text-green-700 rounded-md font-semibold">
                  ✓ Gönderildi
                </span>
                {allowResend && (
                  <Button
                    data-testid="resend-button"
                    onClick={handleSendToServer}
                    disabled={sending}
                    variant="outline"
                    className="border-blue-500 text-blue-600 hover:bg-blue-50"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${sending ? 'animate-spin' : ''}`} />
                    {sending ? 'Gönderiliyor...' : 'Tekrar Gönder'}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Info Cards - Upper Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="glass-card" data-testid="info-card-company">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Şirket</p>
                  <p className="font-semibold text-gray-800 truncate">{upload.company_name || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {upload.invoice_number && (
            <Card className="glass-card" data-testid="info-card-invoice">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Fatura No</p>
                    <p className="font-semibold text-gray-800">{upload.invoice_number}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {upload.location && (
            <Card className="glass-card" data-testid="info-card-location">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Lokasyon</p>
                    <p className="font-semibold text-gray-800">{upload.location}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {upload.invoice_date && (
            <Card className="glass-card" data-testid="info-card-invoice-date">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Prefactuurdatum</p>
                    <p className="font-semibold text-gray-800">{upload.invoice_date}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Info Cards - Lower Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {upload.week_number && (
            <Card className="glass-card" data-testid="info-card-week">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Hafta</p>
                    <p className="font-semibold text-gray-800">Hafta {upload.week_number}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="glass-card" data-testid="info-card-amount">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Fatura Tutarı</p>
                  <p className="font-semibold text-gray-800">{upload.total_amount || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {upload.distributie_amount && (() => {
            // Calculate employees total
            const employeesTotal = upload.employees?.reduce((sum, emp) => sum + (emp.total || 0), 0) || 0;
            const distributieValue = parseFloat(upload.distributie_amount.replace('.', '').replace(',', '.')) || 0;
            const mismatch = Math.abs(employeesTotal - distributieValue) > 0.01;
            
            return (
              <Card className={`glass-card ${mismatch ? 'border-2 border-red-500' : ''}`} data-testid="info-card-distributie">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${mismatch ? 'bg-red-100' : 'bg-pink-100'}`}>
                      <DollarSign className={`w-6 h-6 ${mismatch ? 'text-red-600' : 'text-pink-600'}`} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Distributie</p>
                      <p className={`font-semibold ${mismatch ? 'text-red-600' : 'text-gray-800'}`}>€ {upload.distributie_amount}</p>
                      {mismatch && (
                        <p className="text-xs text-red-600 mt-1">⚠️ Tutar uyuşmuyor!</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {upload.crediteurennr && (
            <Card className="glass-card" data-testid="info-card-crediteur">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-cyan-100 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Crediteurennr</p>
                    <p className="font-semibold text-gray-800">{upload.crediteurennr}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Warning if totals don't match */}
        {upload.distributie_amount && (() => {
          const employeesTotal = upload.employees?.reduce((sum, emp) => sum + (emp.total || 0), 0) || 0;
          const distributieValue = parseFloat(upload.distributie_amount.replace('.', '').replace(',', '.')) || 0;
          const mismatch = Math.abs(employeesTotal - distributieValue) > 0.01;
          
          if (mismatch) {
            return (
              <Card className="glass-card border-2 border-red-500 bg-red-50" data-testid="warning-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="text-4xl">⚠️</div>
                    <div>
                      <p className="font-bold text-red-700">Tutar Uyuşmazlığı!</p>
                      <p className="text-sm text-red-600 mt-1">
                        Çalışanların toplamı (€{employeesTotal.toFixed(2)}) ile Distributie tutarı (€{distributieValue.toFixed(2)}) uyuşmuyor.
                      </p>
                      <p className="text-xs text-red-500 mt-1">
                        Fark: €{Math.abs(employeesTotal - distributieValue).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }
          return null;
        })()}

        {/* Warning for Zero Fixed Charge */}
        {hasZeroFixedCharge && (
          <Card className="glass-card border-2 border-orange-500 bg-orange-50" data-testid="zero-fixed-warning-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-10 h-10 text-orange-600" />
                <div>
                  <p className="font-bold text-orange-700">Sabit Ücret (Vast) Uyarısı!</p>
                  <p className="text-sm text-orange-600 mt-1">
                    Bazı çalışanlarda Sabit Ücret (Vast) değeri <span className="font-bold">€0,00</span> olarak görünüyor.
                  </p>
                  <p className="text-xs text-orange-500 mt-1">
                    Bu kayıtlar tabloda kırmızı ile işaretlenmiştir.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Card className="glass-card" data-testid="data-tabs">
          <CardContent className="pt-6">
            <Tabs defaultValue="employees" className="w-full">
              <TabsList className="grid w-full grid-cols-3" data-testid="tabs-list">
                <TabsTrigger value="employees" data-testid="tab-employees">Detaylı Çalışan Verileri</TabsTrigger>
                <TabsTrigger value="regions" data-testid="tab-regions">Bölge Özeti</TabsTrigger>
                <TabsTrigger value="raw" data-testid="tab-raw">Ham JSON</TabsTrigger>
              </TabsList>

              <TabsContent value="employees" className="space-y-4" data-testid="employees-content">
                {/* Filter Section */}
                {upload.employees && upload.employees.length > 0 && (() => {
                  // Get unique values for filters
                  const uniqueNames = [...new Set(upload.employees.map(e => e.name))].sort();
                  const uniqueRegions = [...new Set(upload.employees.map(e => e.region))].sort();
                  const uniqueDates = [...new Set(upload.employees.map(e => e.date))].sort();
                  
                  // Apply filters
                  const filteredEmployees = upload.employees.filter(emp => {
                    if (selectedName !== "all" && emp.name !== selectedName) return false;
                    if (selectedRegion !== "all" && emp.region !== selectedRegion) return false;
                    if (selectedDate !== "all" && emp.date !== selectedDate) return false;
                    return true;
                  });
                  
                  const activeFiltersCount = [selectedName, selectedRegion, selectedDate].filter(f => f !== "all").length;
                  
                  return (
                    <>
                      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-3 mb-4">
                            <Filter className="w-5 h-5 text-blue-600" />
                            <h3 className="font-semibold text-gray-800">Filtreler</h3>
                            {activeFiltersCount > 0 && (
                              <Badge variant="default" className="bg-blue-600">
                                {activeFiltersCount} aktif filtre
                              </Badge>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            {/* Name Filter */}
                            <div>
                              <label className="text-sm font-medium text-gray-700 mb-2 block">İsim</label>
                              <Select value={selectedName} onValueChange={setSelectedName}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Tümü" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">Tümü ({uniqueNames.length})</SelectItem>
                                  {uniqueNames.map(name => (
                                    <SelectItem key={name} value={name}>{name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            {/* Region Filter */}
                            <div>
                              <label className="text-sm font-medium text-gray-700 mb-2 block">Bölge</label>
                              <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Tümü" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">Tümü ({uniqueRegions.length})</SelectItem>
                                  {uniqueRegions.map(region => (
                                    <SelectItem key={region} value={region}>{region}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            {/* Date Filter */}
                            <div>
                              <label className="text-sm font-medium text-gray-700 mb-2 block">Tarih</label>
                              <Select value={selectedDate} onValueChange={setSelectedDate}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Tümü" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">Tümü ({uniqueDates.length})</SelectItem>
                                  {uniqueDates.map(date => (
                                    <SelectItem key={date} value={date}>{date}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            {/* Clear Filters Button */}
                            <div className="flex items-end">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setSelectedName("all");
                                  setSelectedRegion("all");
                                  setSelectedDate("all");
                                  toast.success("Filtreler temizlendi");
                                }}
                                disabled={activeFiltersCount === 0}
                                className="w-full"
                              >
                                <X className="w-4 h-4 mr-2" />
                                Filtreleri Temizle
                              </Button>
                            </div>
                          </div>
                          
                          {/* Results count */}
                          <div className="mt-4 pt-4 border-t border-blue-200">
                            <p className="text-sm text-gray-600">
                              <span className="font-semibold text-blue-700">{filteredEmployees.length}</span> kayıt gösteriliyor
                              {filteredEmployees.length < upload.employees.length && (
                                <span className="text-gray-500"> (toplam {upload.employees.length} kayıttan)</span>
                              )}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      
                      {/* Employee Table with Filtered Data */}
                      <div className="rounded-lg border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead data-testid="table-head-name">İsim</TableHead>
                              <TableHead data-testid="table-head-date">Tarih</TableHead>
                              <TableHead data-testid="table-head-region">Bölge</TableHead>
                              <TableHead data-testid="table-head-quantity" className="text-right">Adet</TableHead>
                              <TableHead data-testid="table-head-unit-price" className="text-right">Birim Fiyat</TableHead>
                              <TableHead data-testid="table-head-fixed" className="text-right">Sabit Ücret</TableHead>
                              <TableHead data-testid="table-head-total" className="text-right">Toplam</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredEmployees.length > 0 ? (() => {
                              // Group filtered employees by name
                              const grouped = {};
                              filteredEmployees.forEach((emp, idx) => {
                                if (!grouped[emp.name]) {
                                  grouped[emp.name] = [];
                                }
                                grouped[emp.name].push({ ...emp, originalIndex: idx });
                              });
                        
                        // Render grouped employees with subtotals
                        const rows = [];
                        Object.entries(grouped).forEach(([name, emps]) => {
                          // Add employee rows
                          emps.forEach((emp, localIdx) => {
                            const isZeroFixed = emp.fixed_charge === 0 || emp.fixed_charge === 0.0;
                            rows.push(
                              <TableRow 
                                key={`emp-${emp.originalIndex}`} 
                                data-testid={`employee-row-${emp.originalIndex}`}
                                className={isZeroFixed ? 'bg-red-50' : ''}
                              >
                                <TableCell className="font-medium" data-testid={`employee-name-${emp.originalIndex}`}>{emp.name}</TableCell>
                                <TableCell data-testid={`employee-date-${emp.originalIndex}`}>{emp.date}</TableCell>
                                <TableCell data-testid={`employee-region-${emp.originalIndex}`}>
                                  <Badge variant="secondary">{emp.region}</Badge>
                                </TableCell>
                                <TableCell 
                                  className="text-right cursor-pointer hover:bg-blue-100 transition-colors rounded" 
                                  data-testid={`employee-quantity-${emp.originalIndex}`}
                                  onClick={() => copyToClipboard(emp.quantity)}
                                  title="Kopyalamak için tıklayın"
                                >
                                  <span className="flex items-center justify-end gap-1">
                                    {emp.quantity}
                                    <Copy className="w-3 h-3 text-gray-400" />
                                  </span>
                                </TableCell>
                                <TableCell className="text-right" data-testid={`employee-unit-price-${emp.originalIndex}`}>€{emp.unit_price?.toFixed(2)}</TableCell>
                                <TableCell 
                                  className={`text-right ${isZeroFixed ? 'text-red-600 font-bold' : ''}`} 
                                  data-testid={`employee-fixed-${emp.originalIndex}`}
                                >
                                  {isZeroFixed && <AlertTriangle className="w-4 h-4 inline mr-1 text-red-600" />}
                                  €{emp.fixed_charge?.toFixed(2)}
                                </TableCell>
                                <TableCell className="text-right font-semibold" data-testid={`employee-total-${emp.originalIndex}`}>€{emp.total?.toFixed(2)}</TableCell>
                              </TableRow>
                            );
                          });
                          
                          // Add subtotal row if more than 1 entry
                          if (emps.length > 1) {
                            const totalQty = emps.reduce((sum, e) => sum + (e.quantity || 0), 0);
                            const totalAmount = emps.reduce((sum, e) => sum + (e.total || 0), 0);
                            rows.push(
                              <TableRow key={`subtotal-${name}`} className="bg-blue-50 font-bold border-t-2 border-blue-300" data-testid={`employee-subtotal-${name}`}>
                                <TableCell colSpan={2} className="text-blue-700">
                                  {name} TOPLAM:
                                </TableCell>
                                <TableCell></TableCell>
                                <TableCell className="text-right text-blue-700">{totalQty} adet</TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell className="text-right text-blue-700">€{totalAmount.toFixed(2)}</TableCell>
                              </TableRow>
                            );
                          }
                        });
                        
                        return rows;
                      })() : (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-12" data-testid="no-filtered-employees">
                            <div className="flex flex-col items-center gap-3">
                              <Filter className="w-12 h-12 text-gray-300" />
                              <p className="text-gray-500 font-medium">Filtreye uygun kayıt bulunamadı</p>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedName("all");
                                  setSelectedRegion("all");
                                  setSelectedDate("all");
                                }}
                              >
                                Filtreleri Temizle
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                </>
                  );
                })()}
                
                {/* No employees at all */}
                {(!upload.employees || upload.employees.length === 0) && (
                  <div className="text-center py-12 text-gray-500" data-testid="no-employees-message">
                    Çalışan verisi bulunamadı
                  </div>
                )}
              </TabsContent>

              <TabsContent value="regions" className="space-y-4" data-testid="regions-content">
                <div className="grid gap-4">
                  {upload.regions && upload.regions.length > 0 ? (
                    upload.regions.map((region, idx) => (
                      <Card key={idx} data-testid={`region-card-${idx}`}>
                        <CardHeader>
                          <CardTitle className="flex items-center justify-between" data-testid={`region-title-${idx}`}>
                            <span className="flex items-center gap-2">
                              <MapPin className="w-5 h-5" />
                              {region.region}
                            </span>
                            <Badge variant="outline" data-testid={`region-total-${idx}`}>€{region.total?.toFixed(2)}</Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm text-gray-600" data-testid={`region-employee-count-${idx}`}>
                              <Users className="w-4 h-4" />
                              <span>{region.employee_count} çalışan</span>
                            </div>
                            {region.employees && region.employees.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-3" data-testid={`region-employees-${idx}`}>
                                {region.employees.map((emp, empIdx) => (
                                  <Badge key={empIdx} variant="secondary" data-testid={`region-employee-${idx}-${empIdx}`}>
                                    {emp}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card data-testid="no-regions-card">
                      <CardContent className="py-12 text-center">
                        <p className="text-gray-500" data-testid="no-regions-message">Bölge verisi bulunamadı</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="raw" data-testid="raw-content">
                <pre className="bg-gray-50 p-6 rounded-lg overflow-auto max-h-[600px] text-sm" data-testid="raw-json-display">
                  {JSON.stringify(upload.raw_json, null, 2)}
                </pre>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UploadDetail;
